/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { encryptionService, type EncryptionState } from '@/services/encryption';

/**
 * EncryptionContext
 *
 * Manages the lifecycle of the EncryptionService in relation to user authentication.
 * - Initializes the encryption service when a user authenticates
 * - Destroys key material when the user logs out
 * - Exposes encryption readiness state to the UI
 *
 * Must wrap DMProvider so that encryption is ready before DM operations.
 */

interface EncryptionContextValue {
  /** Whether the encryption service has been initialized with a key */
  ready: boolean;
  /** Error message if initialization failed (null if ok) */
  error: string | null;
  /** Retry initialization (e.g., after coming back online) */
  retry: () => void;
}

const EncryptionContext = createContext<EncryptionContextValue>({
  ready: false,
  error: null,
  retry: () => {},
});

export function useEncryption(): EncryptionContextValue {
  return useContext(EncryptionContext);
}

export function EncryptionProvider({ children }: { children: ReactNode }) {
  const { authState } = useAuth();
  const [state, setState] = useState<EncryptionState>({
    ready: false,
    error: null,
    keyVersion: 0,
  });

  // Counter to let retry() re-trigger the initialization effect
  const [initAttempt, setInitAttempt] = useState(0);

  // Initialize or destroy encryption in response to auth state changes.
  // Uses an effect-scoped `cancelled` flag to prevent stale initialization
  // attempts from updating React state (e.g. if destroy() is called while
  // initialize() is still in-flight from a previous auth cycle).
  useEffect(() => {
    let cancelled = false;

    if (authState.status === 'authenticated') {
      (async () => {
        try {
          setState((prev) => ({ ...prev, error: null }));
          await encryptionService.initialize();
          if (!cancelled) setState(encryptionService.getState());
        } catch {
          if (!cancelled) setState(encryptionService.getState());
        }
      })();
    } else {
      encryptionService.destroy();
      setState({ ready: false, error: null, keyVersion: 0 });
    }

    return () => {
      cancelled = true;
    };
  }, [authState.status, initAttempt]);

  const retry = useCallback(() => {
    if (authState.status === 'authenticated') {
      encryptionService.destroy();
      setInitAttempt((prev) => prev + 1);
    }
  }, [authState.status]);

  const value = useMemo(
    () => ({
      ready: state.ready,
      error: state.error,
      retry,
    }),
    [state.ready, state.error, retry]
  );

  return <EncryptionContext.Provider value={value}>{children}</EncryptionContext.Provider>;
}
